import React, { useState } from 'react';
import './QuoteForm.css';
import { useI18n } from '../../i18n/I18n';
import emailjs from '@emailjs/browser';

// EmailJS env (fallback empty). Please set in .env: VITE_EMAILJS_SERVICE_ID, VITE_EMAILJS_TEMPLATE_ID, VITE_EMAILJS_PUBLIC_KEY
const EMAILJS_SERVICE_ID  = process.env.REACT_APP_EMAILJS_SERVICE_ID  || '';
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || '';
const EMAILJS_PUBLIC_KEY  = process.env.REACT_APP_EMAILJS_PUBLIC_KEY  || '';

type QuoteFormState = {
  fullName: string;
  phone: string;
  email: string;
  pickupLocation: string;
  pickupDate: string;
  dropoffDate: string;
  vehicleClass: string;
  notes: string;
};

const initialState: QuoteFormState = {
  fullName: '',
  phone: '',
  email: '',
  pickupLocation: '',
  pickupDate: '',
  dropoffDate: '',
  vehicleClass: '',
  notes: '',
};

const maskPhone = (val: string) => {
  const digits = val.replace(/\D/g, '').slice(0, 11);
  const p = digits.padEnd(11, '');
  const a = p.slice(0, 1);
  const b = p.slice(1, 4);
  const c = p.slice(4, 7);
  const d = p.slice(7, 9);
  const e = p.slice(9, 11);
  return a ? `0${b ? b : ''}${c ? ' ' + c : ''}${d ? ' ' + d : ''}${e ? ' ' + e : ''}`.trim() : '';
};

const QuoteForm: React.FC = () => {
  const [form, setForm] = useState<QuoteFormState>(initialState);
  const [errors, setErrors] = useState<{ [k: string]: string }>({});
  const [hp, setHp] = useState(''); // honeypot
  const [sending, setSending] = useState(false);
  const { t } = useI18n();

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      setForm((f) => ({ ...f, phone: maskPhone(value) }));
    } else {
      setForm((f) => ({ ...f, [name]: value }));
    }
  };

  const validate = (): boolean => {
    const newErrors: { [k: string]: string } = {};
    if (!form.fullName.trim()) newErrors.fullName = 'Zorunlu alan';
    if (!form.phone.trim() || form.phone.replace(/\D/g, '').length < 11) newErrors.phone = 'Geçerli telefon giriniz';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = 'Geçersiz e‑posta';
    if (hp) newErrors.honeypot = 'Lütfen alanı boş bırakın';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildMessage = () => {
    const lines = [
      'MEZ Araç Kiralama - Teklif Talebi',
      `Ad Soyad: ${form.fullName}`,
      `Telefon: ${form.phone}`,
      form.email ? `E-posta: ${form.email}` : undefined,
      form.pickupLocation ? `Alış Lokasyonu: ${form.pickupLocation}` : undefined,
      form.pickupDate ? `Alış Tarihi: ${form.pickupDate}` : undefined,
      form.dropoffDate ? `İade Tarihi: ${form.dropoffDate}` : undefined,
      form.vehicleClass ? `Araç Sınıfı: ${form.vehicleClass}` : undefined,
      form.notes ? `Notlar: ${form.notes}` : undefined,
    ].filter(Boolean);
    return lines.join('\n');
  };

  const afterSubmit = () => {
    window.location.href = '/tesekkurler';
  };

  const submitWhatsApp = () => {
    if (!validate()) return;
    const message = encodeURIComponent(buildMessage());
    const phoneTR = '905334904428';
    window.open(`https://wa.me/${phoneTR}?text=${message}`, '_blank');
    afterSubmit();
  };

  const submitEmail = async () => {
    if (!validate()) return;
    
    console.log('EmailJS Config:', {
      serviceId: EMAILJS_SERVICE_ID ? 'Var' : 'YOK',
      templateId: EMAILJS_TEMPLATE_ID ? 'Var' : 'YOK',
      publicKey: EMAILJS_PUBLIC_KEY ? 'Var' : 'YOK'
    });

    if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY) {
      console.warn('EmailJS yapılandırılmamış. Mailto fallback kullanılıyor.');
      alert('EmailJS yapılandırılmamış. E-posta istemciniz açılacak.');
      // Fallback: mailto
      const subject = encodeURIComponent('Teklif Talebi - MEZ Araç Kiralama');
      const body = encodeURIComponent(buildMessage());
      window.location.href = `mailto:mezkiralama@mez.com.tr?subject=${subject}&body=${body}`;
      afterSubmit();
      return;
    }

    try {
      setSending(true);
      console.log('EmailJS gönderiliyor...');
      const response = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name: form.fullName,
          phone: form.phone,
          from_email: form.email || '',
          pickup_location: form.pickupLocation,
          pickup_date: form.pickupDate,
          return_date: form.dropoffDate,
          car_class: form.vehicleClass,
          notes: form.notes || '',
          subject: 'Araç Kiralama Talebi',
          submitted_at: new Date().toLocaleString('tr-TR'),
        },
        { publicKey: EMAILJS_PUBLIC_KEY }
      );
      console.log('EmailJS başarılı:', response);
      alert('E-postanız başarıyla gönderildi!');
      afterSubmit();
    } catch (e: any) {
      console.error('EmailJS hatası:', e);
      alert(`E‑posta gönderilirken bir sorun oluştu: ${e.text || e.message || 'Bilinmeyen hata'}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="quote-section">
      <div className="quote-container">
        <h2>{t('quote.title')}</h2>
        <p className="quote-sub">{t('quote.subtitle')}</p>
        <div className="quote-grid">
          <div className="form-group">
            <label>Ad Soyad</label>
            <input name="fullName" value={form.fullName} onChange={onChange} placeholder="Ad Soyad" />
            {errors.fullName && <span className="error-text">{errors.fullName}</span>}
          </div>
          <div className="form-group">
            <label>Telefon</label>
            <input name="phone" value={form.phone} onChange={onChange} placeholder="05xx xxx xx xx" inputMode="numeric" />
            {errors.phone && <span className="error-text">{errors.phone}</span>}
          </div>
          <div className="form-group">
            <label>E‑posta (opsiyonel)</label>
            <input name="email" value={form.email} onChange={onChange} placeholder="eposta@ornek.com" />
            {errors.email && <span className="error-text">{errors.email}</span>}
          </div>
          <div className="form-group">
            <label>Alış Lokasyonu</label>
            <select name="pickupLocation" value={form.pickupLocation} onChange={onChange}>
              <option value="">Seçiniz</option>
              <option value="Florya/Istanbul">Florya/Istanbul</option>
              <option value="Hadımköy/Istanbul">Hadımköy/Istanbul</option>
            </select>
          </div>
          <div className="form-group">
            <label>Alış Tarihi</label>
            <input type="date" name="pickupDate" value={form.pickupDate} onChange={onChange} />
          </div>
          <div className="form-group">
            <label>İade Tarihi</label>
            <input type="date" name="dropoffDate" value={form.dropoffDate} onChange={onChange} />
          </div>
          <div className="form-group">
            <label>Araç Sınıfı</label>
            <select name="vehicleClass" value={form.vehicleClass} onChange={onChange}>
              <option value="">Seçiniz</option>
              <option value="Ekonomi">Ekonomi</option>
              <option value="Orta">Orta</option>
              <option value="SUV">SUV</option>
              <option value="Premium">Premium</option>
            </select>
          </div>
          <div className="form-group form-group--full">
            <label>Notlar (opsiyonel)</label>
            <textarea name="notes" value={form.notes} onChange={onChange} rows={3} placeholder="Eklemek istedikleriniz" />
          </div>
          {/* Honeypot alanı */}
          <div style={{ position: 'absolute', left: '-9999px', opacity: 0 }} aria-hidden="true">
            <input name="website" value={hp} onChange={(e) => setHp(e.target.value)} tabIndex={-1} autoComplete="off" />
          </div>
        </div>
        <div className="quote-actions">
          <button className="btn btn-primary" onClick={submitWhatsApp}>WhatsApp ile Gönder</button>
          <button className="btn btn-outline" onClick={submitEmail} disabled={sending}>{sending ? 'Gönderiliyor…' : 'E‑posta ile Gönder'}</button>
        </div>
      </div>
    </section>
  );
};

export default QuoteForm; 